'use strict';

import VZAddBase from './VZAddBase';
import { getJourney } from 'Root/journey/journeyHelper';
import { tryParseJSON } from 'Util/util';

export default class VZAddGroup extends VZAddBase {
    constructor() {
        super();
        this.configuratorType = document.querySelector('[data-configurator-type]').dataset.configuratorType;
        this.addToCartUrl = this.dataset.addToCartUrl;
        this.removeFromCartUrl = this.dataset.removeFromCartUrl;
        this.csSolutionId = null;
    }

    connectedCallback() {
        this.setDurationProperties();
        this.addEventListener('click', this.onClick);
        document.querySelector('vz-group-summary').addEventListener('variationChange', this.setDurationProperties.bind(this));
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
    }

    /**
     * @private setDurationProperties
     * @description Will set and update properties related to contract duration
     */
    setDurationProperties() {
        this.selectedDuration = document.querySelector('#contractDuration input:checked')?.id;
    }

    /**
     * @function submitForm
     * @description Submits the product form when the add to basket modal submit button is clicked.
     * @param {Event} event - A click event on the add to basket modal submit button.
     */
    async submitForm(event) {
        event.preventDefault();
        this.spinner.start();
        const selectedOptionalBusinessRulesAddons = this.constructor.getAllProducts();
        const optionalBusinessRulesAddonsInCart = tryParseJSON(this.dataset.nonCsItemsInCart, []);

        const toBeAdded = selectedOptionalBusinessRulesAddons.filter((addon) => !optionalBusinessRulesAddonsInCart.some(({ id }) => id === addon.pid));
        const toBeRemoved = optionalBusinessRulesAddonsInCart.filter((addon) => !selectedOptionalBusinessRulesAddons.some(({ pid }) => pid === addon.id));

        const isPartOfJourney =
            document.querySelector('#internet')?.checked ||
            document.querySelector('#business-mobile')?.checked ||
            document.querySelector('[name="isPartOfJourney"]')?.value === 'true';
        const inJourney = getJourney()?.length > 0 && isPartOfJourney;

        // If we are in editing mode, remove the old group first
        if (this.editMode) {
            await fetch(document.querySelector('#remove-group').action, {
                method: 'GET'
            });
        }

        if (inJourney) {
            const belongsToGuidedJourney = document.createElement('input');
            belongsToGuidedJourney.setAttribute('type', 'hidden');
            belongsToGuidedJourney.setAttribute('name', 'belongsToGuidedJourney');
            belongsToGuidedJourney.setAttribute('value', inJourney);
            this.form.appendChild(belongsToGuidedJourney);
        }

        // If we are in editing mode, remove all other business rules products first
        if (this.editMode) {
            toBeRemoved.forEach(async (product) => {
                await this.submitLinkedProducts(
                    toBeRemoved,
                    inJourney,
                    `${this.removeFromCartUrl}&${new URLSearchParams({
                        pid: product.id,
                        uuid: product.uuid
                    })}`,
                    'GET'
                );
            });
        }

        if (toBeAdded.length > 0) {
            await this.submitLinkedProducts.bind(this)(toBeAdded, inJourney, this.addToCartUrl, 'POST');
        }

        await this.updateContractDurations();
        [...this.form.querySelectorAll('input[name^="hardware_"')].forEach((field) => field.setAttribute('disabled', 'disabled'));

        this.businessRulesProductsString = document.querySelector('vz-product-card')?.getAttribute('businessrules-products');
        this.businessRulesProducts = this.businessRulesProductsString && tryParseJSON(this.businessRulesProductsString, {});
        const contractsToAdd = [];
        const duplicatesToAdd = [];
        if (this.configuratorType.includes('business-mobile') && this.businessRulesProducts) {
            // Submit BM Hardware contract product
            let optionalBusinessRulesAddons = tryParseJSON(this.form.querySelector('#optionalBusinessRulesAddons')?.value, []);
            // Include additional products (e.g. BM2 data-only SIM) so backend can link them to the current BM group after add-to-cart.
            const additionalProducts = tryParseJSON(this.form.querySelector('#additionalProducts')?.value, []);
            const linkableAdditionalProducts = additionalProducts.filter((product) => product && product.pid && product.quantity);
            optionalBusinessRulesAddons = optionalBusinessRulesAddons
                .concat(linkableAdditionalProducts)
                .filter((product, index, arr) => arr.findIndex((item) => `${item.pid}-${item.deviceIndex || ''}` === `${product.pid}-${product.deviceIndex || ''}`) === index);

            let contractPid;
            Object.keys(this.businessRulesProducts)
                .filter((key) => this.businessRulesProducts[key].isMobileHardware)
                .forEach((key) => {
                    const isMobileHardwareContract = Number.parseInt(this.businessRulesProducts[key].recurringPrice, 10) > 0;
                    optionalBusinessRulesAddons = optionalBusinessRulesAddons.map((product) => {
                        if (key.includes(product.pid)) {
                            // BM Harware is the only businessRuleProduct which can be added to basket several times
                            // Other businessRuleProducts do not support that, that's why this.businessRulesProducts includes each product ID only ones
                            // If deviceIndex is filled in, then the BM Hardware with the same ID was already processes as part of toBeAdded array
                            if (!product.deviceIndex) {
                                product.recurringPrice = Number.parseInt(this.businessRulesProducts[key].recurringPrice, 10);
                                product.oneOffPrice = Number.parseFloat(this.businessRulesProducts[key].oneOffPrice, 10);
                                product.contractDuration = Number.parseInt(this.businessRulesProducts[key].contractDuration, 10);
                                product.deviceIndex = key.split('---device')[1];
                                product.quantity = this.businessRulesProducts[key].qty;
                            } else {
                                // Saving current BM Hardware with duplicated ID to duplicatesToAdd array
                                // This product was not added to basket as part of toBeAdded array
                                duplicatesToAdd.push({
                                    pid: product.pid,
                                    quantity: this.businessRulesProducts[key].qty,
                                    recurringPrice: Number.parseInt(this.businessRulesProducts[key].recurringPrice, 10),
                                    oneOffPrice: Number.parseFloat(this.businessRulesProducts[key].oneOffPrice, 10),
                                    contractDuration: Number.parseInt(this.businessRulesProducts[key].contractDuration, 10),
                                    deviceIndex: key.split('---device')[1]
                                });
                            }
                        }
                        return product;
                    });
                    if (isMobileHardwareContract) {
                        contractPid = `RCTSTBET${Number.parseInt(this.businessRulesProducts[key].contractDuration, 10) === 12 ? '1' : '2'}J${Number.parseInt(
                            this.businessRulesProducts[key].recurringPrice,
                            10
                        )}EUF`;
                        optionalBusinessRulesAddons.push({
                            pid: contractPid,
                            quantity: this.businessRulesProducts[key].qty
                        });
                        contractsToAdd.push({ pid: contractPid, quantity: this.businessRulesProducts[key].qty });
                    }
                });
            if (duplicatesToAdd.length) duplicatesToAdd.forEach((duplicate) => optionalBusinessRulesAddons.push(duplicate));
            this.form.querySelector('#optionalBusinessRulesAddons').value = JSON.stringify(optionalBusinessRulesAddons);
        }

        if (duplicatesToAdd.length > 0) {
            // Adding to basket BM Hardware devices with duplicated IDs
            await this.submitLinkedProducts.bind(this)(duplicatesToAdd, inJourney, this.addToCartUrl, 'POST');
        }
        if (contractsToAdd.length > 0) {
            // Adding to basket zakelijk products
            await this.submitLinkedProducts.bind(this)(contractsToAdd, inJourney, this.addToCartUrl, 'POST');
        }

        const formData = new FormData(this.form);
        // If the previous call returned csSolutionId, this call has to use the same solution
        if (this.csSolutionId !== null) {
            formData.set('csSolutionId', this.csSolutionId);
        }

        fetch(this.form.action, {
            method: 'post',
            body: formData
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) throw new Error('Error');
                // After having added the product to the cart, show the "added to basket" modal or journey modal
                const modal = document.getElementById(inJourney ? 'journey-modal' : 'add-to-basket-modal');
                const hasNonCsProductsSelected = !inJourney && Array.from(document.getElementsByClassName('js-non_cs_product') ?? '').some(({ checked }) => checked);
                const bodyText = document.getElementById('addToBasketBodyNonCPQText')?.value;
                if (!inJourney && response.hideAddToBasketModal) {
                    window.location.href = response.basketUrl;
                } else {
                    modal.render(hasNonCsProductsSelected ? { bodyText } : {});
                }

                /* eslint-disable no-underscore-dangle */
                if (response.ecommerceObject && window._ddm) {
                    window._ddm.trigger('ecommerce.add_to_cart', {
                        data: response.ecommerceObject.ecommerce.addToCart
                    });
                }
                /* eslint-enable no-underscore-dangle */
            })
            .catch(() => {
                this.constructor.handleNetworkError();
            })
            .finally(() => {
                this.spinner.stop();
            });
    }

    /**
     * @function updateContractDurations
     * @return {Promise} - A promise
     * @description Will return a promise that can update the contract durations of other products
     */
    updateContractDurations() {
        // Create an empty array here, because we always need to return a Promise.
        let promises = [];
        const newDuration = document.querySelector('.js-contract-duration input:checked')?.id;

        if (this.configuratorType === 'business-mobile' && newDuration !== '1') {
            promises = [...document.querySelectorAll('.js-contract-update')].reduce((result, updateSection) => {
                // Get all "contract update" forms based on the newly selected duration
                const formEqualsSelectedDuration = updateSection.querySelector(`form[id^="${newDuration}-"]`);
                if (!formEqualsSelectedDuration) return result;

                const formData = new FormData();

                [...formEqualsSelectedDuration.elements].forEach((currentForm) => {
                    formData.append(currentForm.name, currentForm.value);
                });

                result.push(
                    fetch(formEqualsSelectedDuration.action, {
                        body: formData,
                        method: 'post'
                    })
                );

                return result;
            }, []);
        }

        return Promise.all(promises);
    }

    /**
     * @function onClick
     * @description Handles the onClick
     * @param {Event} event - The click event fired from the add to basket button.
     */
    onClick(event) {
        event.preventDefault();
        const quantityField = document.querySelector('.js-quantity-field');
        const quantityValid = quantityField && quantityField.checkValidity();

        if (!quantityValid) return;
        [...this.form.querySelectorAll('[name="addon"]')].forEach((field) => field.setAttribute('disabled', 'disabled'));
        [...this.form.querySelectorAll('[name="mobileDeviceAddons"]')].forEach((field) => field.setAttribute('disabled', 'disabled'));
        this.submitForm.call(this, event);
    }

    /**
     * @function submitLinkedProducts
     * @return {Promise} - A resolved promise
     * @param {Array} products - Array of linked products
     * @param {boolean} inJourney - Is in journey
     * @param {string} url - Request URL
     * @param {string} type - Type of Request
     * @description Submits all the products which are linked to the configured one.
     */
    submitLinkedProducts(products, inJourney, url, type) {
        const formData = new FormData();
        formData.set('productData', JSON.stringify(products));
        formData.set('belongsToGuidedJourney', inJourney);

        // Pass selected addons for linked-product basket adds (e.g. Data SIM roaming),
        // and include mandatory hardware addons when present.
        const selectedAddonsRaw = document.querySelector('[name="addons"]')?.value || '[]';
        const mobileDeviceAddonsRaw = document.querySelector('[name="mobileDeviceAddons"]')?.value || '[]';
        try {
            const selectedAddons = JSON.parse(selectedAddonsRaw);
            const mobileDeviceAddons = JSON.parse(mobileDeviceAddonsRaw);
            const mergedAddons = [...new Set([...(selectedAddons || []), ...(mobileDeviceAddons || [])])];
            if (mergedAddons.length) {
                formData.set('addons', JSON.stringify(mergedAddons));
            }
        } catch (error) {
            // Fallback to the selected addons payload if parsing fails.
            if (selectedAddonsRaw && selectedAddonsRaw !== '[]') {
                formData.set('addons', selectedAddonsRaw);
            }
        }
        // If the previous call returned csSolutionId, this call has to use the same solution
        if (this.csSolutionId !== null) {
            formData.set('csSolutionId', this.csSolutionId);
        }

        return fetch(url, {
            method: type,
            ...(type === 'POST' && { body: formData })
        })
            .then((response) => response.json())
            .then((response) => {
                if (response && response.csSolutionId) this.csSolutionId = response.csSolutionId;
            })
            .catch(() => {
                this.spinner.stop();
                this.constructor.handleNetworkError();
            });
    }
}

if (!window.customElements.get('vz-add-group')) {
    window.customElements.define('vz-add-group', VZAddGroup);
}
